// VideoTutorialModal — modal overlay con iframe de YouTube para el
// tutorial de un ejercicio. Sprint 3 spec-medicion Bloque 1 (12/7/2026):
// reused pattern del iframe de VideoOnlyView, pero mostrado on-demand
// desde un botón "Ver cómo se hace" en el pre-session gate.
//
// UX:
//   - Backdrop fijo full-screen con blur; click cierra.
//   - ESC cierra.
//   - Iframe aspect-video con controles nativos + fullscreen.
//   - Fuente youtube-nocookie.com (privacy-enhanced, sin autoplay).
//   - Botón X arriba a la derecha.
//
// No se muestra durante la sesión live (cámara corriendo) — solo en el
// pre-session gate. Ratificado por Ale 12/7 Q6.

import { useEffect } from "react";
import { X } from "lucide-react";
import { getYoutubeEmbedUrl } from "@/lib/pose/youtube";

interface Props {
  /** URL completa del video (watch?v=, youtu.be/, embed/). Modal no se
   *  renderea si es undefined o no parseable — el caller es responsable
   *  de no ofrecer el botón si el ejercicio no tiene video. */
  videoUrl: string | undefined;
  /** Nombre del ejercicio para el title del iframe (accesibilidad). */
  exerciseName: string;
  /** Estado open/closed controlado por el padre. */
  open: boolean;
  onClose: () => void;
}

export function VideoTutorialModal({
  videoUrl,
  exerciseName,
  open,
  onClose,
}: Props) {
  const embedUrl = getYoutubeEmbedUrl(videoUrl);

  // ESC cierra el modal.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open || !embedUrl) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Video tutorial: ${exerciseName}`}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-150"
    >
      {/* Content: stopPropagation para que el click en el iframe/botón X
          NO cierre el modal (solo el backdrop). */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar video tutorial"
          className="absolute -top-10 right-0 md:top-2 md:right-2 md:z-10 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
        >
          <X className="w-5 h-5" strokeWidth={2} />
        </button>
        <div className="rounded-2xl overflow-hidden border border-border bg-black aspect-video shadow-2xl">
          <iframe
            src={embedUrl}
            title={`Video tutorial: ${exerciseName}`}
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      </div>
    </div>
  );
}
